import { Interest } from "@/schemas/profile/attributes/UserInterestAttributeSet";
import { Language } from "@/schemas/profile/attributes/UserLanguageAttributeSet";
import { Seeking } from "@/schemas/profile/attributes/UserSeekingAttributeSet";
import { Height } from "@/schemas/profile/attributes/UserHeightAttributeSet";
import {
  CompatibilityType,
  RecommendationCategory,
  RecommendationHighlightType,
  UserRecommendedItem,
} from "@/schemas/user/explore";
import { UserImageType } from "@/schemas/user/image";
import { Ethnicity } from "@/schemas/profile/attributes/UserEthnicityAttributeSet";
import { BodyType } from "@/schemas/profile/attributes/UserBodyAttributeSet";
import { Weight } from "@/schemas/profile/attributes/UserWeightAttributeSet";
import { RolePosition } from "@/schemas/profile/attributes/UserRoleAttributeSet";

/**
 * Category priority metadata for profiles
 * Maps profile IDs to the categories where they should be featured and their priority rank
 */
export type ProfileCategoryPriority = {
  profileId: string;
  categories: {
    category: RecommendationCategory;
    priority: number; // Lower number = higher priority (1 = first)
  }[];
};

/**
 * Category priorities for Asian themed profiles
 * Defines which profiles should appear first in specific recommendation categories
 *
 * Available categories: TRENDING, DATING, FRIENDS, ONLINE_NOW, NEW_USERS, NEARBY
 */
export const ASIAN_PROFILE_CATEGORY_PRIORITIES: ProfileCategoryPriority[] = [
  {
    profileId: "MKT001", // neonrift - Tokyo
    categories: [
      { category: RecommendationCategory.TRENDING, priority: 1 },
      { category: RecommendationCategory.FRIENDS, priority: 2 },
      { category: RecommendationCategory.NEW_USERS, priority: 1 },
    ],
  },
  {
    profileId: "MKT002", // hallyu - Seoul
    categories: [
      { category: RecommendationCategory.TRENDING, priority: 2 },
      { category: RecommendationCategory.FRIENDS, priority: 1 },
      { category: RecommendationCategory.ONLINE_NOW, priority: 1 },
    ],
  },
  {
    profileId: "MKT003", // monsoon - Bangkok
    categories: [
      { category: RecommendationCategory.DATING, priority: 2 },
      { category: RecommendationCategory.ONLINE_NOW, priority: 2 },
    ],
  },
  {
    profileId: "MKT004", // lanternboy - Taipei
    categories: [
      { category: RecommendationCategory.FRIENDS, priority: 3 },
      { category: RecommendationCategory.NEW_USERS, priority: 2 },
    ],
  },
  {
    profileId: "MKT005", // equatorial - Singapore
    categories: [
      { category: RecommendationCategory.DATING, priority: 1 },
      { category: RecommendationCategory.ONLINE_NOW, priority: 4 },
    ],
  },
  {
    profileId: "MKT006", // harborview - Hong Kong
    categories: [
      { category: RecommendationCategory.FRIENDS, priority: 4 },
      { category: RecommendationCategory.ONLINE_NOW, priority: 3 },
    ],
  },
  {
    profileId: "MKT008", // matcha - Osaka
    categories: [
      { category: RecommendationCategory.TRENDING, priority: 3 },
      { category: RecommendationCategory.DATING, priority: 3 },
    ],
  },
  {
    profileId: "MKT009", // durian - Kuala Lumpur
    categories: [
      { category: RecommendationCategory.NEARBY, priority: 1 },
      { category: RecommendationCategory.NEW_USERS, priority: 3 },
    ],
  },
];

/**
 * Asian themed mock data - used for demos and presentations
 * Contains 9 polished profiles with professional images and complete attributes
 * Showcases the app at its best for marketing purposes
 * Represents diverse gay community experiences across major Asian cities
 */
export const MOCK_ASIAN_EXPLORE_ITEMS: UserRecommendedItem[] = [
  {
    id: "MKT001",
    rank: 1,
    username: "neonrift",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/01.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/01.webp",
      },
      id: "mkt-001",
      likeCount: 184,
      type: UserImageType.PUBLIC_IMAGE,
      username: "neonrift",
    },
    matchingPercentage: 95,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 97,
      },
      {
        rank: 1,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 92,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.COMMON_INTERESTS,
      },
    ],
    commonInterests: [
      Interest.TRAVELING,
      Interest.FITNESS,
      Interest.GAMING,
      Interest.PHOTOGRAPHY,
    ],
    distance: 3.1,
    profileAttributes: {
      aboutMe:
        "Shinjuku Ni-chome regular, creative director by day, karaoke king by night. Tokyo born and raised, out since uni. You'll find me at AiiRO Cafe or dancing at Arty Farty. Anime nerd, ramen snob, and proud of it. Looking for someone to explore Golden Gai with or just vibe at Yoyogi Park.",
      birthday: "1995-08-12",
      bodyType: BodyType.SLIM,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_9,
      weight: Weight.LBS_145,
      relationship: null,
      role: RolePosition.VERSATILE,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.NETWORKING,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.JAPANESE, Language.ENGLISH],
      interests: [
        Interest.TRAVELING,
        Interest.FITNESS,
        Interest.GAMING,
        Interest.PHOTOGRAPHY,
        Interest.EATING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT002",
    rank: 2,
    username: "hallyu",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/02.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/02.webp",
      },
      id: "mkt-002",
      likeCount: 231,
      type: UserImageType.PUBLIC_IMAGE,
      username: "hallyu",
    },
    matchingPercentage: 93,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 95,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SAME_ETHNICITY,
      },
    ],
    commonInterests: [Interest.DANCING, Interest.FITNESS, Interest.EATING],
    distance: 1.4,
    profileAttributes: {
      aboutMe:
        "Itaewon creative living loud and proud in Seoul. K-beauty influencer, dance choreographer, and the friend who always knows the best samgyeopsal spot. Catch me at Trance or Queen on weekends. Out in a culture that's still catching up, and I'm helping it get there. Let's grab soju and talk about life.",
      birthday: "1996-03-28",
      bodyType: BodyType.TONED,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_10,
      weight: Weight.LBS_155,
      relationship: null,
      role: RolePosition.BOTTOM,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.CHAT,
        Seeking.EVENTS,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.KOREAN, Language.ENGLISH],
      interests: [
        Interest.DANCING,
        Interest.FITNESS,
        Interest.EATING,
        Interest.MUSIC,
        Interest.PHOTOGRAPHY,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT003",
    rank: 3,
    username: "monsoon",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/03.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/03.webp",
      },
      id: "mkt-003",
      likeCount: 167,
      type: UserImageType.PUBLIC_IMAGE,
      username: "monsoon",
    },
    matchingPercentage: 89,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 91,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.RECENT_ACTIVITY,
      },
    ],
    commonInterests: [Interest.TRAVELING, Interest.EATING, Interest.DANCING],
    distance: 8.5,
    profileAttributes: {
      aboutMe:
        "Silom nightlife veteran and Muay Thai instructor. Bangkok born, queer, and thriving. Thailand's LGBTQ scene is magic and I'm right in the middle of it. DJ Cafe on Fridays, Maggie Choo's on Saturdays. Pad thai purist, temple hopper on Sundays. Looking for a gym buddy who can also handle street food at 2am.",
      birthday: "1997-11-05",
      bodyType: BodyType.MUSCULAR,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_8,
      weight: Weight.LBS_160,
      relationship: null,
      role: RolePosition.VERS_TOP,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.GYM_BUDDY,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.THAI, Language.ENGLISH],
      interests: [
        Interest.FITNESS,
        Interest.EATING,
        Interest.TRAVELING,
        Interest.DANCING,
        Interest.HIKING,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT004",
    rank: 4,
    username: "lanternboy",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/04.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/04.webp",
      },
      id: "mkt-004",
      likeCount: 198,
      type: UserImageType.PUBLIC_IMAGE,
      username: "lanternboy",
    },
    matchingPercentage: 92,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 89,
      },
      {
        rank: 1,
        type: CompatibilityType.DATING_FORWARD,
        score: 86,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.MANY_SHARED_INTERESTS,
      },
    ],
    commonInterests: [
      Interest.PHOTOGRAPHY,
      Interest.TRAVELING,
      Interest.READING,
      Interest.EATING,
    ],
    distance: 2.7,
    profileAttributes: {
      aboutMe:
        "Ximending creative and proud Taiwanese queer. Taipei legalized marriage equality and I cried at the rally. Night market food critic, bubble tea addict, indie film buff. Catch me at Commander D or Funky on weekends. Looking for intentional connections in Asia's most progressive city.",
      birthday: "1994-05-20",
      bodyType: BodyType.AVERAGE,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_11,
      weight: Weight.LBS_160,
      relationship: null,
      role: RolePosition.VERSATILE,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.NETWORKING,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.CHINESE, Language.ENGLISH, Language.JAPANESE],
      interests: [
        Interest.PHOTOGRAPHY,
        Interest.TRAVELING,
        Interest.READING,
        Interest.EATING,
        Interest.CRAFTS,
        Interest.WRITING,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT005",
    rank: 5,
    username: "equatorial",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/05.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/05.webp",
      },
      id: "mkt-005",
      likeCount: 215,
      type: UserImageType.PUBLIC_IMAGE,
      username: "equatorial",
    },
    matchingPercentage: 91,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 93,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_LANGUAGE,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SIMILAR_AGE,
      },
    ],
    commonInterests: [Interest.FITNESS, Interest.TRAVELING, Interest.EATING],
    distance: 5.2,
    profileAttributes: {
      aboutMe:
        "Fintech bro in the Lion City. Singaporean Chinese, gym rat, hawker center regular. Pink Dot attendee every year, fighting for visibility in a city that's slowly opening up. Tantric Bar on weekends, East Coast Park runs in the morning. Looking for someone real in a polished city.",
      birthday: "1993-01-15",
      bodyType: BodyType.FIT,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_10,
      weight: Weight.LBS_165,
      relationship: null,
      role: RolePosition.TOP,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.GYM_BUDDY,
        Seeking.EVENTS,
      ],
      languages: [Language.ENGLISH, Language.CHINESE],
      interests: [
        Interest.FITNESS,
        Interest.TRAVELING,
        Interest.EATING,
        Interest.GAMING,
        Interest.CYCLING,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT006",
    rank: 6,
    username: "harborview",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/06.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/06.webp",
      },
      id: "mkt-006",
      likeCount: 176,
      type: UserImageType.PUBLIC_IMAGE,
      username: "harborview",
    },
    matchingPercentage: 88,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 90,
      },
      {
        rank: 1,
        type: CompatibilityType.DATING_FORWARD,
        score: 85,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.COMMON_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.SAME_ETHNICITY,
      },
    ],
    commonInterests: [
      Interest.EATING,
      Interest.PHOTOGRAPHY,
      Interest.TRAVELING,
      Interest.READING,
    ],
    distance: 1.9,
    profileAttributes: {
      aboutMe:
        "Central district creative director with a dim sum addiction. Hong Kong born, London educated, back home and queer as ever. Zoo Bar in Lan Kwai Fong, hiking the Dragon's Back on weekends. This city moves fast but I'm looking for someone to slow down with over yum cha.",
      birthday: "1992-09-03",
      bodyType: BodyType.SLIM,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_8,
      weight: Weight.LBS_140,
      relationship: null,
      role: RolePosition.VERS_BOTTOM,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.CHAT,
        Seeking.COFFEE_DATES,
      ],
      languages: [Language.CHINESE, Language.ENGLISH, Language.WU_CHINESE],
      interests: [
        Interest.EATING,
        Interest.PHOTOGRAPHY,
        Interest.TRAVELING,
        Interest.READING,
        Interest.HIKING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT007",
    rank: 7,
    username: "archipelago",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/07.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/07.webp",
      },
      id: "mkt-007",
      likeCount: 153,
      type: UserImageType.PUBLIC_IMAGE,
      username: "archipelago",
    },
    matchingPercentage: 87,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 88,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_INTERESTS,
      },
    ],
    commonInterests: [Interest.MUSIC, Interest.EATING, Interest.DANCING],
    distance: 12.4,
    profileAttributes: {
      aboutMe:
        "Makati creative and proud Pinoy. Manila's queer scene is vibrant and I'm at the center of it. Nectar nightclub on Saturdays, Sunday brunch at BGC. Graphic designer, karaoke champion, and the friend who always organizes Pride floats. Looking for someone who matches my energy and isn't afraid to be out loud.",
      birthday: "1998-02-08",
      bodyType: BodyType.TONED,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_7,
      weight: Weight.LBS_145,
      relationship: null,
      role: RolePosition.BOTTOM,
      residence: null,
      seeking: [
        Seeking.DATES,
        Seeking.FRIENDS,
        Seeking.EVENTS,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH],
      interests: [
        Interest.MUSIC,
        Interest.EATING,
        Interest.DANCING,
        Interest.PHOTOGRAPHY,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT008",
    rank: 8,
    username: "matcha",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/08.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/08.webp",
      },
      id: "mkt-008",
      likeCount: 192,
      type: UserImageType.PUBLIC_IMAGE,
      username: "matcha",
    },
    matchingPercentage: 90,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.FRIENDS_FORWARD,
        score: 91,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_INTERESTS,
      },
      {
        rank: 1,
        type: RecommendationHighlightType.RECENT_ACTIVITY,
      },
    ],
    commonInterests: [Interest.COOKING, Interest.EATING, Interest.TRAVELING],
    distance: 4.6,
    profileAttributes: {
      aboutMe:
        "Osaka chef running a tiny izakaya in Namba. Kansai boy with a big laugh and bigger appetite. Doyamacho is my neighborhood, FrenZ Bar is my living room. Takoyaki over everything, onsen trips on days off. Vers who believes food is love. Come taste my omakase and let's see where the night goes.",
      birthday: "1995-12-01",
      bodyType: BodyType.AVERAGE,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_7,
      weight: Weight.LBS_150,
      relationship: null,
      role: RolePosition.VERSATILE,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.COFFEE_DATES,
        Seeking.NETWORKING,
      ],
      languages: [Language.JAPANESE, Language.ENGLISH],
      interests: [
        Interest.COOKING,
        Interest.EATING,
        Interest.TRAVELING,
        Interest.GARDENING,
        Interest.PHOTOGRAPHY,
      ],
    },
    isLocked: false,
  },
  {
    id: "MKT009",
    rank: 9,
    username: "durian",
    image: {
      attributes: {
        height: 800,
        width: 600,
        previewUrl:
          "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/09.webp",
        url: "https://dow-labs-dev.s3.amazonaws.com/kai-mobile/images/screens/landing/01-asian-theme/09.webp",
      },
      id: "mkt-009",
      likeCount: 141,
      type: UserImageType.PUBLIC_IMAGE,
      username: "durian",
    },
    matchingPercentage: 86,
    scores: [
      {
        rank: 0,
        type: CompatibilityType.DATING_FORWARD,
        score: 87,
      },
    ],
    highlights: [
      {
        rank: 0,
        type: RecommendationHighlightType.SHARED_LANGUAGE,
      },
    ],
    commonInterests: [Interest.TRAVELING, Interest.EATING, Interest.HIKING],
    distance: 6.8,
    profileAttributes: {
      aboutMe:
        "Bukit Bintang architect navigating KL's growing queer scene. Malaysian Chinese, multilingual, and unapologetically out. Blue Boy Pub on weekends, Batu Caves hikes to clear my head. Passionate about design, durian, and dismantling stereotypes. Looking for someone who appreciates good laksa and even better conversation.",
      birthday: "1994-07-22",
      bodyType: BodyType.FIT,
      ethnicity: Ethnicity.ASIAN,
      height: Height.FT_5_9,
      weight: Weight.LBS_155,
      relationship: null,
      role: RolePosition.SIDE,
      residence: null,
      seeking: [
        Seeking.FRIENDS,
        Seeking.DATES,
        Seeking.CHAT,
        Seeking.LGBTQ_COMMUNITY,
      ],
      languages: [Language.ENGLISH, Language.CHINESE, Language.INDONESIAN],
      interests: [
        Interest.TRAVELING,
        Interest.EATING,
        Interest.HIKING,
        Interest.READING,
        Interest.CRAFTS,
      ],
    },
    isLocked: false,
  },
];

export function getAsianExploreItems(): UserRecommendedItem[] {
  return [...MOCK_ASIAN_EXPLORE_ITEMS];
}
